/**
 * Напоминание отложить — в день прихода, а не в конце месяца.
 *
 * Копится то, что убрано **до** трат, а не то, что осталось после: остатка в
 * конце месяца обычно нет, и цель, живущая на остатке, не наполняется никогда.
 *
 * Но убирает деньги человек, а не приложение. Оно не имеет доступа к счетам и
 * не должно делать вид, что распоряжается ими: перевод делает человек, а
 * подтверждение в приложении лишь записывает то, что уже случилось. Поэтому
 * здесь считается просьба, а не действие.
 *
 * Сумму можно изменить: в этом месяце могла случиться покупка, и требовать
 * полный взнос значило бы получить в ответ «пропустить» вместо «отложить
 * меньше». Изменение относится к одному приходу, план не переписывается.
 */
import { ZERO, add, clampPos, sub, type Money } from './money.js';
import { dayWords, isAfter, type PlainDate } from './date.js';
import { orderGoals } from './goals.js';
import { lastActualIncome, type IncomeFact } from './income.js';
import type { Goal, IncomeStream } from './types.js';

export interface SavingsAsk {
  goalId: string;
  name: string;
  /** Сколько просим отложить за этот приход. */
  amount: Money;
  /** Взнос за этот приход уже сделан или сознательно пропущен. */
  funded: boolean;
  /** Почему просим именно сейчас — показывается человеку. */
  why: string;
}

export interface SavingsNudge {
  /** Приход в этом цикле уже был — значит, есть из чего откладывать. */
  incomeArrived: boolean;
  /** За какой приход откладываем. По нему же сбрасывается следующий цикл. */
  cycleStart: PlainDate | null;
  asks: SavingsAsk[];
  /** Сколько ещё не отложено по этому приходу. */
  pending: Money;
  /** Сколько уже отложено или пропущено. */
  settled: Money;
}

export interface SavingsInput {
  today: PlainDate;
  incomeStreams: IncomeStream[];
  /**
   * Приходы, которые случились. Именно они открывают цикл: просить отложить
   * можно только из денег, которые пришли.
   */
  incomeFacts?: readonly IncomeFact[];
  goals: Goal[];
}

/**
 * Кого и о чём просить в этом цикле.
 *
 * Цель с будущей датой начала не просит ничего: она ещё не началась. Цель без
 * взноса не просит тоже — ей достаются свободные деньги, а не обязательство.
 */
export function savingsNudge(input: SavingsInput): SavingsNudge {
  const cycleStart = lastIncomeDate(input.incomeFacts ?? [], input.today);
  const active = input.goals.filter((g) => g.status === 'active');

  const asks: SavingsAsk[] = orderGoals(active)
    .filter((g) => g.monthlyContribution > ZERO && g.contributionMode !== 'residual')
    .filter((g) => !g.startDate || !isAfter(g.startDate, input.today))
    .filter((g) => clampPos(sub(g.targetAmount, g.currentAmount)) > ZERO)
    .map((g) => ({
      goalId: g.id,
      name: g.name,
      // Больше остатка просить нельзя: цель, перебравшая свою сумму, выглядит
      // как ошибка расчёта, а не как забота.
      amount: minOf(g.monthlyContribution, clampPos(sub(g.targetAmount, g.currentAmount))),
      funded: isFunded(g, cycleStart),
      why: whyNow(g),
    }));

  return {
    incomeArrived: cycleStart !== null,
    cycleStart,
    asks,
    pending: add(...asks.filter((a) => !a.funded).map((a) => a.amount)),
    settled: add(...asks.filter((a) => a.funded).map((a) => a.amount)),
  };
}

/**
 * Взнос за этот приход сделан.
 *
 * Старое булево `fundedInPeriod` уважается ради записей, заведённых до
 * появления даты: считать такую цель непрофинансированной значило бы попросить
 * денег второй раз.
 */
export function isFunded(g: Goal, cycleStart: PlainDate | null): boolean {
  if (g.fundedCycle && cycleStart && g.fundedCycle === cycleStart) return true;
  if (g.fundedCycle && !cycleStart) return true;
  return g.fundedInPeriod;
}

/**
 * Дата последнего прихода — по факту, а не по расписанию.
 *
 * Пока зарплата не пришла, просить откладывать не из чего: это и есть разница
 * между «заплати себе первым» и «найди деньги где хочешь».
 *
 * Раньше дата считалась из `dayOfMonth`: наступило пятое число — значит,
 * деньги есть. Расписание этого не доказывает. Человек, которому задержали
 * зарплату, получал просьбу отложить из денег, которых нет, а сдельщику
 * приложение назначало цикл по числу, которого он никогда не называл.
 *
 * Факты приходят снаружи: только вызывающий знает, что было переводом между
 * своими счетами, а что возвратом. Пустой список — приходов не было, и цикл
 * не начался.
 */
export function lastIncomeDate(
  facts: readonly IncomeFact[], today: PlainDate,
): PlainDate | null {
  return lastActualIncome(facts, today);
}

const minOf = (a: Money, b: Money): Money => (a <= b ? a : b);

function whyNow(g: Goal): string {
  if (g.kind === 'emergency_fund') {
    return 'Запас на чёрный день собирается только так: сразу с прихода, а не из остатка';
  }
  if (g.kind === 'debt_payoff') {
    return 'Досрочный взнос по долгу — деньги, которые иначе уйдут на проценты';
  }
  if (g.targetDate) return `К сроку ${dayWords(g.targetDate)} нужен именно такой взнос`;
  return 'Взнос за этот приход';
}
